import { inject, Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { EMPTY, from, Observable, of, switchMap } from 'rxjs';

import { AlertController } from '@ionic/angular';
import { I18nService } from '@ts/shared/i18n/util-core';
import { LoggingService } from '@ts/shared/logging/util-core';
import { syncStringPromise } from '@ts/shared/util-async';

import { AlertType } from './alert.model';

export interface AlertServiceStandardParams {
  message: string | Promise<string>;
  /**
   * If blank, will be replaced with a default error header message.
   */
  header?: string | Promise<string>;
}
export interface AlertServiceConfirmParams {
  /**
   * Translated action sentence fragment.
   *
   * @example
   *
   * `delete user address xyz`.
   *
   * The sentence will then become:
   *
   * "Are you sure you want to delete user address xyz?"
   */
  action: string | Promise<string>;

  type: AlertType;

  /**
   * If true, then a warning under will be shown that says something like:
   * "This action cannot be undone."
   */
  isShowPermanentWarning?: boolean;

  /**
   * If true, then a warning under will shown action message only without prefix or suffix sentence
   */
  isShowActionMessageOnly?: boolean;
}

export const ROLE_CANCEL = 'cancel';
export const ROLE_CONFIRM = 'confirm';

/**
 * Wrapper for Ion AlertController, for showing various kinds of alerts to the user.
 */
@Injectable({
  providedIn: 'root',
})
export class AlertService {
  private alertController = inject(AlertController);
  private i18nService = inject(I18nService);
  private domSanitizer = inject(DomSanitizer);
  private loggingService = inject(LoggingService);

  // Css class for alert button
  primaryButtonCssClass = 'button-primary';
  secondaryButtonCssClass = 'button-secondary';
  dangerButtonCssClass = 'button-danger';

  // For an alert built with its own buttons.
  getCssClass(type: AlertType): string {
    return `alert-wide alert-${type}`;
  }

  private FOCUS_ON_PRESENT_BUTTON_CLASS_NAME =
    'borma-dago-alert-service-focus-on-present-button';

  private async presentAndFocus(alert: HTMLIonAlertElement) {
    await alert.present();
    const focusable = alert.querySelector(
      `.${this.FOCUS_ON_PRESENT_BUTTON_CLASS_NAME}`,
    ) as HTMLElement | undefined;
    focusable?.focus();
  }

  private async createAlert$(
    type: AlertType,
    { message, header }: AlertServiceStandardParams,
  ): Promise<string> {
    let headerContent: string;
    if (!header) {
      headerContent = await this.i18nService.translate$(
        `sharedUtilAlert.${type}.title`,
      );
    } else {
      headerContent = await syncStringPromise(header);
    }

    const messageContent = await syncStringPromise(message);
    const alert = await this.alertController.create({
      message: messageContent,
      header: headerContent,
      cssClass: this.getCssClass(type),
      buttons: [
        {
          text: 'ok',
          role: ROLE_CONFIRM,
          cssClass: [this.FOCUS_ON_PRESENT_BUTTON_CLASS_NAME],
        },
      ],
    });

    await this.presentAndFocus(alert);

    return messageContent;
  }

  /**
   * Shows an error and prompt it to the user.
   */
  async error$(alertServiceStandardParams: AlertServiceStandardParams) {
    // For error, we want to attach a self help link at the end.
    const message = `
      <div>
        ${this.domSanitizer.sanitize(
          SecurityContext.HTML,
          await syncStringPromise(alertServiceStandardParams.message),
        )}
      </div>
      <br/>
      <a href="/shop/faq/contact-us">
        ${await this.i18nService.translate$('shop.footer.contactUs')}
      </a>
    `;

    await this.createAlert$('danger', {
      ...alertServiceStandardParams,
      message,
    });
    this.loggingService.log(
      'error',
      await syncStringPromise(alertServiceStandardParams.message),
    );
  }

  async success$(alertServiceStandardParams: AlertServiceStandardParams) {
    const message = await this.createAlert$(
      'success',
      alertServiceStandardParams,
    );
    this.loggingService.log('info', message);
  }

  /**
   * Shows a confirm dialog to the user.
   *
   * The returned observable will emit once if the user confirms.
   * Otherwise, it'll just close without emitting.
   *
   * No need to manage subscription because ionic will kill the subscription when the
   * alert is closed.
   *
   * @example
   *
   * ```
   * alertService.confirm$({action: i18nService.translate$('address.delete')}).subscribe(() => {
   *   actionService.deleteAddress(...)
   * })
   * ```
   */
  confirm$(
    alertServiceConfirmParams: AlertServiceConfirmParams,
  ): Observable<void> {
    return from(this.confirmDialogShow(alertServiceConfirmParams)).pipe(
      switchMap((alert) => {
        return from(alert.onDidDismiss());
      }),
      switchMap((dismissResult): Observable<void> => {
        if (dismissResult.role === ROLE_CONFIRM) {
          return of(undefined);
        } else {
          return EMPTY;
        }
      }),
    );
  }

  private async confirmDialogShow({
    action,
    isShowPermanentWarning,
    type,
    isShowActionMessageOnly,
  }: AlertServiceConfirmParams): Promise<HTMLIonAlertElement> {
    const actionContent = await syncStringPromise(action);

    const headerContent = isShowActionMessageOnly
      ? actionContent
      : await this.i18nService.translate$('sharedUtilAlert.confirm.message', {
          action: actionContent,
        });

    const messageContent = isShowPermanentWarning
      ? await this.i18nService.translate$(
          'sharedUtilAlert.confirm.permanentWarning',
        )
      : undefined;

    // Focus follows the primary button: for a permanent action that is Cancel,
    // so an Enter still held from the previous step cannot confirm it unread.
    const cancelButtonCssClass = isShowPermanentWarning
      ? [this.FOCUS_ON_PRESENT_BUTTON_CLASS_NAME, this.primaryButtonCssClass]
      : this.secondaryButtonCssClass;
    const confirmButtonCssClass = isShowPermanentWarning
      ? this.dangerButtonCssClass
      : [this.FOCUS_ON_PRESENT_BUTTON_CLASS_NAME, this.primaryButtonCssClass];

    const alert = await this.alertController.create({
      header: headerContent,
      message: messageContent,
      cssClass: this.getCssClass(type),
      buttons: [
        {
          text: await this.i18nService.translate$(
            'sharedUtilAlert.confirm.cancel',
          ),
          role: ROLE_CANCEL,
          cssClass: cancelButtonCssClass,
        },
        {
          text: await this.i18nService.translate$(
            'sharedUtilAlert.confirm.confirm',
          ),
          role: ROLE_CONFIRM,
          cssClass: confirmButtonCssClass,
        },
      ],
    });

    await this.presentAndFocus(alert);

    return alert;
  }
}
